import type { ComponentPropsWithoutRef, ReactElement } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, NewTabAnchor } from '@/components/link';
import { cn } from '@/lib/utils';
import { TruncatedBlockContent } from '@/components/truncated-block-content';
import { MARKDOWN_PROSE_CLASS } from './styles';

function isInternalHref(href: string | undefined): boolean {
    if (!href) return false;
    return href.startsWith('/') || href.startsWith('#');
}

function MarkdownAnchor({ href, children, className }: ComponentPropsWithoutRef<'a'>): ReactElement {
    if (href && isInternalHref(href)) {
        return (
            <Link to={href} className={className}>
                {children}
            </Link>
        );
    }

    return (
        <NewTabAnchor href={href ?? '#'} className={className}>
            {children}
        </NewTabAnchor>
    );
}

const markdownComponents: Components = {
    a: MarkdownAnchor,
};

export interface MarkdownContentProps {
    /** Markdown source text */
    content: string;
    className?: string;
    linesToRender?: number;
    /** When true, truncation and read more/less apply only below the lg breakpoint. */
    mobileOnly?: boolean;
}

/**
 * Renders markdown to semantic HTML. Safe by default (no raw HTML).
 */
export function MarkdownContent({ content, className, linesToRender, mobileOnly }: MarkdownContentProps): ReactElement {
    return (
        <TruncatedBlockContent
            className={cn(MARKDOWN_PROSE_CLASS, className)}
            linesToRender={linesToRender}
            mobileOnly={mobileOnly}
            data-testid="markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {content.trim()}
            </ReactMarkdown>
        </TruncatedBlockContent>
    );
}
